import { computed, reactive, ref } from 'vue'
import { openPullRequestsApi } from '@/api/openPullRequests'
import { apiErrorMessage } from '@/lib/errors'
import { createRequestGuard, withAsyncState } from '@/lib/asyncState'
import { bucketByActionQueueStatus } from '@/lib/pullRequestQueue'
import type { PullRequest, PullRequestFilters } from '@/types/models'

// Singleton, following useLinearTaskPullRequests.ts's shape: shared state so
// Builds' ticketless-PR list retains its list across navigation within a
// session.
const pullRequests = ref<PullRequest[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const prErrors = reactive(new Map<string, string>())

// IDs of PRs with a retry-ci/retry-review request in flight — a Set so
// button-disabling per row is O(1) regardless of list size.
const retryingCiIds = reactive(new Set<string>())
const retryingReviewIds = reactive(new Set<string>())

// Guards against a stale filter request's response landing after a newer
// one's and clobbering it.
const requestGuard = createRequestGuard()

// Incremented on logout (reset()) to stale out any in-flight retry requests,
// so a late response from a request the previous session started can't land
// and set error for the next user.
const sessionEpoch = ref(0)

const showDrafts = ref(false)

export function useBuildPullRequests() {
  async function fetchPullRequests(filters: PullRequestFilters = {}): Promise<void> {
    const token = requestGuard.start()
    await withAsyncState(
      error,
      'Failed to load open pull requests.',
      async () => {
        const data = await openPullRequestsApi.list(filters)
        if (!requestGuard.isCurrent(token)) return
        pullRequests.value = data
      },
      loading,
      undefined,
      () => !requestGuard.isCurrent(token),
    )
  }

  // Marks a PR read the moment it's opened/expanded from the list — updates
  // local state optimistically (so the row stops looking unread immediately)
  // rather than waiting on a refetch, and swallows a failure since a missed
  // read-marker isn't worth surfacing an error over a PR the user is already
  // navigating away to look at.
  async function markPullRequestViewed(id: string): Promise<void> {
    const pullRequest = pullRequests.value.find((pr) => pr.id === id)
    if (pullRequest) pullRequest.is_unread = false

    try {
      await openPullRequestsApi.markViewed(id)
    } catch (err) {
      console.error('Failed to mark pull request viewed:', err)
    }
  }

  // Called on logout so the next session's Builds page doesn't briefly
  // show the previous user's data before its own fetch resolves, and so a
  // late response from a request the previous session started can't land
  // and repopulate it.
  function reset(): void {
    requestGuard.start()
    sessionEpoch.value++
    pullRequests.value = []
    loading.value = false
    error.value = null
    prErrors.clear()
    retryingCiIds.clear()
    retryingReviewIds.clear()
    showDrafts.value = false
  }

  // Re-requests the PR's failed checks on GitHub. Errors (e.g. the checks
  // are no longer there to retry) surface via prErrors for this PR.
  async function retryCi(id: string): Promise<void> {
    const epoch = sessionEpoch.value
    retryingCiIds.add(id)
    prErrors.delete(id)
    try {
      await openPullRequestsApi.retryCi(id)
    } catch (err) {
      if (epoch === sessionEpoch.value) {
        prErrors.set(id, apiErrorMessage(err, 'Failed to retry the build.'))
      }
    } finally {
      retryingCiIds.delete(id)
    }
  }

  async function retryReview(id: string): Promise<void> {
    const epoch = sessionEpoch.value
    retryingReviewIds.add(id)
    prErrors.delete(id)
    try {
      await openPullRequestsApi.retryReview(id)
    } catch (err) {
      if (epoch === sessionEpoch.value) {
        prErrors.set(id, apiErrorMessage(err, 'Failed to re-run the review.'))
      }
    } finally {
      retryingReviewIds.delete(id)
    }
  }

  const ticketlessPullRequests = computed(() => pullRequests.value.filter((pr) => pr.linear_task === null))
  const visibleTicketlessPullRequests = computed(() =>
    showDrafts.value ? ticketlessPullRequests.value : ticketlessPullRequests.value.filter((pr) => !pr.draft),
  )
  const prBuckets = computed(() => bucketByActionQueueStatus(visibleTicketlessPullRequests.value, (pr) => pr.action_queue_status ?? 'in_progress'))

  return {
    pullRequests,
    loading,
    error,
    prErrors,
    retryingCiIds,
    retryingReviewIds,
    fetchPullRequests,
    markPullRequestViewed,
    retryCi,
    retryReview,
    reset,
    showDrafts,
    needsYouPullRequests: computed(() => prBuckets.value.needsYou),
    readyToMergePullRequests: computed(() => prBuckets.value.readyToMerge),
    inProgressPullRequests: computed(() => prBuckets.value.inProgress),
  }
}
